import React from 'react';
import { ArrowLeft, Flame, FileText } from 'lucide-react';
import { Button } from './ui/Button';
import { ThemeToggle } from './ui/ThemeToggle';

interface TermsOfServiceProps {
  onBack?: () => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{title}</h2>
    <div className="text-gray-600 dark:text-gray-300 space-y-3 leading-relaxed">{children}</div>
  </section>
);

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </button>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">Koru</span>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Terms of Service</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Effective date: January 1, 2025 &nbsp;·&nbsp; Last updated: March 2025
          </p>
          <p className="mt-4 text-gray-600 dark:text-gray-300 leading-relaxed">
            Please read these Terms of Service ("Terms", "Terms of Service") carefully before using the Koru
            habit tracking application (the "App") operated by Daily Mobile LLC ("us", "we", or "our").
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using the App, you agree to be bound by these Terms. If you disagree with any part
              of the terms, you may not access the App. Your use of the App constitutes your agreement to these Terms.
            </p>
          </Section>

          <Section title="2. Description of Service">
            <p>
              Koru is a habit tracking application that allows users to create, track, and manage personal habits
              and goals. Features include:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Habit creation and management</li>
              <li>Streak tracking and statistics</li>
              <li>Push notifications and reminders</li>
              <li>Subscription-based premium features</li>
              <li>Data export capabilities</li>
            </ul>
          </Section>

          <Section title="3. User Accounts">
            <p><strong className="text-gray-800 dark:text-gray-100">Registration:</strong> To use certain features of the App, you must register for an account. You agree to provide accurate, current, and complete information during registration and to keep this information updated.</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Account Security:</strong> You are responsible for safeguarding the password and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Account Deletion:</strong> You may delete your account at any time through the App. Upon deletion, your data will be permanently removed from our systems within 30 days.</p>
          </Section>

          <Section title="4. Subscriptions and Payments">
            <p><strong className="text-gray-800 dark:text-gray-100">Subscription Terms:</strong> Some features of the App require a paid subscription. By subscribing, you agree to pay the subscription fees indicated. Subscriptions automatically renew unless cancelled.</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Cancellation:</strong> You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of the current billing period.</p>
            <p><strong className="text-gray-800 dark:text-gray-100">Refunds:</strong> All purchases are final. Refunds are granted at our sole discretion and only in exceptional circumstances.</p>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to use the App to:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Attempt to gain unauthorized access to the App or its systems</li>
              <li>Interfere with or disrupt the App's functionality</li>
              <li>Upload or transmit any malicious code, viruses, or harmful materials</li>
              <li>Engage in any activity that could damage, disable, or overburden our infrastructure</li>
            </ul>
            <p>We reserve the right to terminate or suspend your account immediately for any violation of these terms.</p>
          </Section>

          <Section title="6. Intellectual Property">
            <p>
              The App and its original content, features, and functionality are owned by Daily Mobile LLC and
              are protected by international copyright, trademark, patent, trade secret, and other intellectual
              property laws. You may not reproduce, distribute, modify, create derivative works of, publicly
              display, publicly perform, republish, download, store, or transmit any of the material on our App.
            </p>
          </Section>

          <Section title="7. User Content">
            <p>
              You retain ownership of any content you create or upload to the App, including habit data and notes.
              By using the App, you grant us a limited license to store and process this data solely for the
              purpose of providing the App's services to you.
            </p>
            <p>
              You represent and warrant that you own or have the necessary rights to any content you submit,
              and that such content does not violate any third-party rights.
            </p>
          </Section>

          <Section title="8. Disclaimer of Warranties">
            <p>
              The App is provided on an "AS IS" and "AS AVAILABLE" basis. We make no warranties, expressed or
              implied, and hereby disclaim all warranties, including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, non-infringement, or course of performance.
            </p>
            <p>
              We do not warrant that the App will be uninterrupted, timely, secure, or error-free, or that any
              defects will be corrected. Your use of the App is at your sole risk.
            </p>
          </Section>

          <Section title="9. Limitation of Liability">
            <p>
              In no event shall Daily Mobile LLC, nor its directors, employees, partners, agents, suppliers, or
              affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages,
              including without limitation, loss of profits, data, use, goodwill, or other intangible losses,
              resulting from:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Your access to or use of, or inability to access or use, the App</li>
              <li>Any conduct or content of any third party on the App</li>
              <li>Any content obtained from the App</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
          </Section>

          <Section title="10. Governing Law">
            <p>
              These Terms shall be governed and construed in accordance with the laws of the United States
              and the State of [Your State], without regard to its conflict of law provisions. Our failure to
              enforce any right or provision of these Terms will not be considered a waiver of those rights.
            </p>
          </Section>

          <Section title="11. Changes to Terms">
            <p>
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we
              will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a
              material change will be determined at our sole discretion. By continuing to access or use our
              App after any revisions become effective, you agree to be bound by the revised terms.
            </p>
          </Section>

          <Section title="12. Contact Us">
            <p>If you have any questions about these Terms, please contact us at:</p>
            <div className="mt-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-100 dark:border-primary-800">
              <p className="font-semibold text-primary-800 dark:text-primary-200">Koru Support</p>
              <p className="text-primary-700 dark:text-primary-300">
                Email:{' '}
                <a
                  href="mailto:support@dailymobilellc.com"
                  className="underline hover:text-primary-900 dark:hover:text-primary-100 transition-colors"
                >
                  support@dailymobilellc.com
                </a>
              </p>
            </div>
          </Section>
        </div>

        {onBack && (
          <div className="mt-8 text-center">
            <Button onClick={onBack} variant="outline" leftIcon={<ArrowLeft size={16} />}>
              Back to Koru
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Koru. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
